import React, { useState } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Chart from 'react-apexcharts';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import BarChartIcon from '@mui/icons-material/BarChart';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

const ChartContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
}));

const LegendContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  justifyContent: 'center',
  marginTop: theme.spacing(2),
  padding: theme.spacing(2),
  backgroundColor: theme.palette.grey[50],
  borderRadius: theme.spacing(1),
}));

// ฟังก์ชันสร้างข้อมูล Bell Curve สำหรับแต่ละหลักสูตร
const generateBellCurveData = (mean, stdDev, color, programCode) => {
  const points = [];
  const start = Math.max(0, mean - 4 * stdDev);
  const end = Math.min(100, mean + 4 * stdDev);
  const step = (end - start) / 100;

  for (let x = start; x <= end; x += step) {
    const y = (1 / (stdDev * Math.sqrt(2 * Math.PI))) * 
             Math.exp(-0.5 * Math.pow((x - mean) / stdDev, 2));
    points.push({ x: x.toFixed(1), y: y.toFixed(6) });
  }

  return {
    name: programCode,
    data: points,
    color: color,
    mean: mean,
    stdDev: stdDev
  };
};

const MultiBellCurveChart = ({ programs = [], facultyAverage = 72.5 }) => {
  const [chartType, setChartType] = useState('line'); // 'line' or 'area'
  const [visiblePrograms, setVisiblePrograms] = useState(
    new Set(programs.map(p => p.program_code))
  );
  const [selectedProgram, setSelectedProgram] = useState('all');

  // สร้างข้อมูล Bell Curve สำหรับแต่ละหลักสูตร
  const bellCurveData = programs.map(program => 
    generateBellCurveData(
      program.avg_score, 
      program.std_deviation, 
      program.color,
      program.program_code
    )
  );

  // Filter data based on visibility and selection
  const filteredData = bellCurveData.filter(curve => {
    if (selectedProgram === 'all') {
      return visiblePrograms.has(curve.name);
    }
    return curve.name === selectedProgram;
  });

  const toggleProgramVisibility = (programCode) => {
    const newVisible = new Set(visiblePrograms);
    if (newVisible.has(programCode)) {
      newVisible.delete(programCode);
    } else {
      newVisible.add(programCode);
    }
    setVisiblePrograms(newVisible);
  };

  const chartOptions = {
    chart: {
      type: chartType,
      height: 450,
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
      },
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: false,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: false,
          reset: true
        }
      },
      background: 'transparent'
    },
    title: {
      text: 'การกระจายคะแนนการประเมิน (Bell Curve)',
      align: 'center',
      style: {
        fontSize: '18px',
        fontWeight: 'bold',
        color: '#1976d2'
      }
    },
    subtitle: {
      text: `คะแนนเฉลี่ยรวมคณะ: ${facultyAverage} คะแนน`,
      align: 'center',
      style: {
        fontSize: '14px',
        color: '#666'
      }
    },
    xaxis: {
      title: {
        text: 'คะแนน',
        style: {
          fontSize: '14px',
          fontWeight: 'bold'
        }
      },
      labels: {
        formatter: function(val) {
          return parseFloat(val).toFixed(0);
        }
      },
      min: 40,
      max: 90
    },
    yaxis: {
      title: {
        text: 'ความหนาแน่นของการกระจาย',
        style: {
          fontSize: '14px',
          fontWeight: 'bold'
        }
      },
      labels: {
        formatter: function(val) {
          return val.toFixed(4);
        }
      }
    },
    stroke: {
      width: chartType === 'line' ? 3 : 2,
      curve: 'smooth'
    },
    fill: {
      type: chartType === 'area' ? 'gradient' : 'solid',
      opacity: chartType === 'area' ? 0.6 : 1,
      gradient: {
        shade: 'light',
        type: 'vertical',
        shadeIntensity: 0.5,
        gradientToColors: undefined,
        inverseColors: false,
        opacityFrom: 0.7,
        opacityTo: 0.1,
      }
    },
    grid: {
      borderColor: '#e0e0e0',
      strokeDashArray: 5,
      xaxis: {
        lines: {
          show: true
        }
      },
      yaxis: {
        lines: {
          show: true
        }
      }
    },
    legend: {
      show: false // เราจะใช้ custom legend
    },
    tooltip: {
      shared: true,
      intersect: false,
      theme: 'light',
      custom: function({ series, seriesIndex, dataPointIndex, w }) {
        const programData = filteredData[seriesIndex];
        const score = parseFloat(w.globals.categoryLabels[dataPointIndex]);
        
        return `
          <div class="arrow_box" style="padding: 10px; background: white; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);">
            <div style="font-weight: bold; color: ${programData.color}; margin-bottom: 5px;">
              ${programData.name}
            </div>
            <div style="margin-bottom: 3px;">
              <strong>คะแนน:</strong> ${score.toFixed(1)}
            </div>
            <div style="margin-bottom: 3px;">
              <strong>ความหนาแน่น:</strong> ${series[seriesIndex][dataPointIndex]}
            </div>
            <div style="margin-bottom: 3px;">
              <strong>ค่าเฉลี่ย:</strong> ${programData.mean.toFixed(1)}
            </div>
            <div>
              <strong>ส่วนเบียงเบน:</strong> ±${programData.stdDev.toFixed(1)}
            </div>
          </div>
        `;
      }
    },
    annotations: {
      xaxis: [{
        x: facultyAverage,
        borderColor: '#FF4560',
        borderWidth: 2,
        strokeDashArray: 8,
        label: {
          borderColor: '#FF4560',
          style: {
            color: '#fff',
            background: '#FF4560',
            fontSize: '12px',
            fontWeight: 'bold'
          },
          text: `เฉลี่ยคณะ: ${facultyAverage}`,
          orientation: 'horizontal',
          position: 'top'
        }
      }]
    },
    colors: filteredData.map(curve => curve.color)
  };

  const series = filteredData.map(curve => ({
    name: curve.name,
    data: curve.data.map(point => ({
      x: parseFloat(point.x),
      y: parseFloat(point.y)
    }))
  }));

  return (
    <Box>
      {/* Controls */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small">
            <InputLabel>เลือกหลักสูตร</InputLabel>
            <Select
              value={selectedProgram}
              onChange={(e) => setSelectedProgram(e.target.value)}
              label="เลือกหลักสูตร"
            >
              <MenuItem value="all">🎓 ทุกหลักสูตร</MenuItem>
              {programs.map(program => (
                <MenuItem key={program.program_code} value={program.program_code}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box 
                      sx={{ 
                        width: 12, 
                        height: 12, 
                        borderRadius: '50%', 
                        bgcolor: program.color 
                      }} 
                    />
                    {program.program_code}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={4}>
          <ToggleButtonGroup
            value={chartType}
            exclusive
            onChange={(e, newType) => newType && setChartType(newType)}
            size="small"
            fullWidth
          >
            <ToggleButton value="line">
              <ShowChartIcon sx={{ mr: 1 }} />
              เส้น
            </ToggleButton>
            <ToggleButton value="area">
              <BarChartIcon sx={{ mr: 1 }} />
              พื้นที่
            </ToggleButton>
          </ToggleButtonGroup>
        </Grid>
      </Grid>

      {/* Chart */}
      <ChartContainer elevation={3}>
        {filteredData.length > 0 ? (
          <Chart
            options={chartOptions}
            series={series}
            type={chartType}
            height={450}
          />
        ) : (
          <Box 
            sx={{ 
              height: 450, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              flexDirection: 'column'
            }}
          >
            <Typography variant="h6" color="text.secondary">
              ไม่มีข้อมูลสำหรับแสดงผล
            </Typography>
            <Typography variant="body2" color="text.secondary">
              กรุณาเลือกหลักสูตรที่ต้องการดู
            </Typography>
          </Box>
        )}
      </ChartContainer>

      {/* Program Legend & Toggle */}
      {selectedProgram === 'all' && (
        <LegendContainer>
          <Typography variant="subtitle2" sx={{ mr: 2, alignSelf: 'center' }}>
            แสดง/ซ่อนหลักสูตร:
          </Typography>
          {programs.map(program => (
            <Tooltip 
              key={program.program_code}
              title={`${program.program_name} (เฉลี่ย: ${program.avg_score.toFixed(1)})`}
              arrow
            >
              <Chip
                label={program.program_code}
                onClick={() => toggleProgramVisibility(program.program_code)}
                icon={
                  visiblePrograms.has(program.program_code) ? 
                    <VisibilityIcon /> : <VisibilityOffIcon />
                }
                sx={{
                  backgroundColor: visiblePrograms.has(program.program_code) ? 
                    program.color : 'grey.300',
                  color: visiblePrograms.has(program.program_code) ? 'white' : 'grey.600',
                  fontWeight: 'bold',
                  '&:hover': {
                    backgroundColor: program.color,
                    color: 'white',
                    opacity: 0.8
                  }
                }}
              />
            </Tooltip>
          ))}
        </LegendContainer>
      )}

      {/* Statistics Summary */}
      <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom color="primary">
          📈 สรุปสถิติ
        </Typography>
        <Grid container spacing={2}>
          {filteredData.map(curve => {
            const program = programs.find(p => p.program_code === curve.name);
            return (
              <Grid item xs={12} sm={6} md={4} key={curve.name}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: curve.color }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      {curve.name}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    เฉลี่ย: <strong>{curve.mean.toFixed(1)}</strong> | 
                    ส่วนเบียงเบน: <strong>±{curve.stdDev.toFixed(1)}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    นิสิต: <strong>{program?.student_count || 0}</strong> คน
                  </Typography>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </Box>
  );
};

export default MultiBellCurveChart;
